import { KTag, KText } from '@/ui-library/components';
import { KInput } from '@/ui-library/components/atoms/KInput';
import { REGEX_PATTERNS } from '@/ui-library/components/constants';
import * as Yup from 'yup';
import { Box, Flex, Icon, Button } from '@chakra-ui/react';
import { useState, useRef } from 'react';
import { useShowToast } from '@/hooks';
import { Copy03, PlusCircle, Trash03 } from '@kula-ai/icons-core';
import {
  Email,
  EmailsListProps,
  EditedEmail,
  EmailChanges,
  NewEmail,
} from './EmailsList.interface';
import { FaCheckCircle } from 'react-icons/fa';
import { TruncatedTextWithTooltip } from '@/ui/TooltipText';

// Type for new email input UI state
interface NewEmailInput {
  tempId: string;
  value: string;
  error: string;
  isEditing: boolean;
}

// Module-level state to track email changes (persists across re-renders and remounts)
const emailChangesState = {
  editedEmails: new Map<number, EditedEmail>(),
  deletedEmailIds: new Set<number>(),
  newPrimaryEmailId: null as number | null,
  newEmails: [] as NewEmail[],
  newPrimaryTempId: null as string | null,
  // Store new email inputs at module level to persist during API calls
  newEmailInputs: [] as NewEmailInput[],
  // Track currently editing email for validation on submit
  currentlyEditingId: null as number | null,
  currentlyEditingTempId: null as string | null,
  currentEditValue: '' as string,
  // Store original emails for duplicate checking in validateAllEmails
  originalEmails: [] as Email[],
};

// Email validation schema using Yup with regex pattern
const emailValidationSchema = Yup.string()
  .email('Please enter a valid email')
  .matches(REGEX_PATTERNS.email, 'Please enter a valid email')
  .max(250, 'Email must be less than 250 characters')
  .required('Email is required');

// Get current email changes for form submission
export const getEmailChanges = (): EmailChanges => {
  // Build newEmails from newEmailInputs to ensure they're always in sync
  // This handles cases where the input hasn't been blurred yet
  const newEmailsFromInputs: NewEmail[] = emailChangesState.newEmailInputs
    .filter((input) => input.value.trim() !== '')
    .map((input) => {
      const trimmedEmail = input.value.trim();
      // Check if we already have validation info for this email
      const existingEmail = emailChangesState.newEmails.find(
        (e) => e.tempId === input.tempId
      );
      // Use existing validation if available and email hasn't changed,
      // otherwise validate the email using Yup
      let isValid = false;
      let errorMessage = '';

      if (existingEmail?.email === trimmedEmail) {
        isValid = existingEmail.isValid;
        if (!isValid) {
          errorMessage = 'Please enter a valid email';
        }
      } else {
        try {
          emailValidationSchema.validateSync(trimmedEmail);
          isValid = true;
        } catch (error: any) {
          isValid = false;
          errorMessage = error.message || 'Please enter a valid email';
        }
      }

      // Update the input error state if validation failed
      if (!isValid) {
        const inputIndex = emailChangesState.newEmailInputs.findIndex(
          (i) => i.tempId === input.tempId
        );
        if (inputIndex >= 0) {
          emailChangesState.newEmailInputs[inputIndex] = {
            ...emailChangesState.newEmailInputs[inputIndex],
            error: errorMessage,
            isEditing: true,
          };
        }
      }

      return {
        tempId: input.tempId,
        email: trimmedEmail,
        isValid,
      };
    });

  return {
    editedEmails: Array.from(emailChangesState.editedEmails.values()),
    deletedEmailIds: Array.from(emailChangesState.deletedEmailIds),
    newPrimaryEmailId: emailChangesState.newPrimaryEmailId,
    newEmails: newEmailsFromInputs,
    newPrimaryTempId: emailChangesState.newPrimaryTempId,
  };
};

// Reset email changes after successful submission
export const resetEmailChanges = () => {
  emailChangesState.editedEmails.clear();
  emailChangesState.deletedEmailIds.clear();
  emailChangesState.newPrimaryEmailId = null;
  emailChangesState.newEmails = [];
  emailChangesState.newPrimaryTempId = null;
  emailChangesState.newEmailInputs = [];
  emailChangesState.currentlyEditingId = null;
  emailChangesState.currentlyEditingTempId = null;
  emailChangesState.currentEditValue = '';
  emailChangesState.originalEmails = [];
};

// Module-level duplicate check for use in validateAllEmails
const checkDuplicateEmail = (
  emailToCheck: string,
  excludeId?: number | null,
  excludeTempId?: string
): boolean => {
  const normalizedEmail = emailToCheck.toLowerCase().trim();

  // Check existing emails (not deleted)
  const existingEmails = emailChangesState.originalEmails
    .filter(
      (e) => e.id !== excludeId && !emailChangesState.deletedEmailIds.has(e.id)
    )
    .map((e) => {
      // Use edited value if available
      const edited = emailChangesState.editedEmails.get(e.id);
      return (edited?.email ?? e.email).toLowerCase().trim();
    });

  // Check new emails
  const newEmails = emailChangesState.newEmailInputs
    .filter((input) => input.tempId !== excludeTempId && input.value.trim())
    .map((input) => input.value.toLowerCase().trim());

  const allEmails = [...existingEmails, ...newEmails];
  return allEmails.includes(normalizedEmail);
};

// Validate all emails and return first invalid email ID (for scrolling)
export const validateAllEmails = (): {
  isValid: boolean;
  firstInvalidId: string | number | null;
} => {
  // First check if there's a currently editing EXISTING email that hasn't been validated yet
  if (emailChangesState.currentlyEditingId !== null) {
    const trimmedEmail = emailChangesState.currentEditValue.trim();
    if (!trimmedEmail) {
      // Store as invalid
      emailChangesState.editedEmails.set(emailChangesState.currentlyEditingId, {
        id: emailChangesState.currentlyEditingId,
        email: '',
        isValid: false,
        error: 'Email is required',
      });
      return {
        isValid: false,
        firstInvalidId: emailChangesState.currentlyEditingId,
      };
    }
    // Validate format
    try {
      emailValidationSchema.validateSync(trimmedEmail);
      // Check for duplicates
      if (
        checkDuplicateEmail(trimmedEmail, emailChangesState.currentlyEditingId)
      ) {
        emailChangesState.editedEmails.set(
          emailChangesState.currentlyEditingId,
          {
            id: emailChangesState.currentlyEditingId,
            email: trimmedEmail,
            isValid: false,
            error: 'Email is duplicate for this candidate',
          }
        );
        return {
          isValid: false,
          firstInvalidId: emailChangesState.currentlyEditingId,
        };
      }
      // Store as valid
      emailChangesState.editedEmails.set(emailChangesState.currentlyEditingId, {
        id: emailChangesState.currentlyEditingId,
        email: trimmedEmail,
        isValid: true,
        error: '',
      });
    } catch (error: any) {
      // Store as invalid
      emailChangesState.editedEmails.set(emailChangesState.currentlyEditingId, {
        id: emailChangesState.currentlyEditingId,
        email: trimmedEmail,
        isValid: false,
        error: error.message || 'Please enter a valid email',
      });
      return {
        isValid: false,
        firstInvalidId: emailChangesState.currentlyEditingId,
      };
    }
  }

  // Check if there's a currently editing NEW email that hasn't been validated yet
  if (emailChangesState.currentlyEditingTempId !== null) {
    const trimmedEmail = emailChangesState.currentEditValue.trim();
    const inputIndex = emailChangesState.newEmailInputs.findIndex(
      (i) => i.tempId === emailChangesState.currentlyEditingTempId
    );

    if (!trimmedEmail) {
      // Update error state for empty email
      if (inputIndex >= 0) {
        emailChangesState.newEmailInputs[inputIndex] = {
          ...emailChangesState.newEmailInputs[inputIndex],
          value: '',
          error: 'Email is required',
        };
      }
      return {
        isValid: false,
        firstInvalidId: emailChangesState.currentlyEditingTempId,
      };
    }
    // Validate format
    try {
      emailValidationSchema.validateSync(trimmedEmail);
      // Check for duplicates
      if (
        checkDuplicateEmail(
          trimmedEmail,
          null,
          emailChangesState.currentlyEditingTempId
        )
      ) {
        if (inputIndex >= 0) {
          emailChangesState.newEmailInputs[inputIndex] = {
            ...emailChangesState.newEmailInputs[inputIndex],
            value: trimmedEmail,
            error: 'Email is duplicate for this candidate',
          };
        }
        return {
          isValid: false,
          firstInvalidId: emailChangesState.currentlyEditingTempId,
        };
      }
      // Update as valid
      if (inputIndex >= 0) {
        emailChangesState.newEmailInputs[inputIndex] = {
          ...emailChangesState.newEmailInputs[inputIndex],
          value: trimmedEmail,
          error: '',
        };
      }
    } catch (error: any) {
      // Update as invalid
      if (inputIndex >= 0) {
        emailChangesState.newEmailInputs[inputIndex] = {
          ...emailChangesState.newEmailInputs[inputIndex],
          value: trimmedEmail,
          error: error.message || 'Please enter a valid email',
        };
      }
      return {
        isValid: false,
        firstInvalidId: emailChangesState.currentlyEditingTempId,
      };
    }
  }

  // Check existing edited emails for validation errors and duplicates
  const editedEmailIds = Array.from(emailChangesState.editedEmails.keys());
  for (const id of editedEmailIds) {
    const editedEmail = emailChangesState.editedEmails.get(id);
    if (editedEmail) {
      if (!editedEmail.isValid) {
        return { isValid: false, firstInvalidId: id };
      }
      // Also check for duplicates (in case another email was edited to create a duplicate)
      if (checkDuplicateEmail(editedEmail.email, id)) {
        emailChangesState.editedEmails.set(id, {
          ...editedEmail,
          isValid: false,
          error: 'Email is duplicate for this candidate',
        });
        return { isValid: false, firstInvalidId: id };
      }
    }
  }

  // Check new emails for validation errors and duplicates
  for (const input of emailChangesState.newEmailInputs) {
    if (input.error || !input.value.trim()) {
      return { isValid: false, firstInvalidId: input.tempId };
    }
    // Validate format
    try {
      emailValidationSchema.validateSync(input.value.trim());
      // Check for duplicates
      if (checkDuplicateEmail(input.value.trim(), null, input.tempId)) {
        const inputIndex = emailChangesState.newEmailInputs.findIndex(
          (i) => i.tempId === input.tempId
        );
        if (inputIndex >= 0) {
          emailChangesState.newEmailInputs[inputIndex] = {
            ...emailChangesState.newEmailInputs[inputIndex],
            error: 'Email is duplicate for this candidate',
          };
        }
        return { isValid: false, firstInvalidId: input.tempId };
      }
    } catch {
      // Update error state
      const inputIndex = emailChangesState.newEmailInputs.findIndex(
        (i) => i.tempId === input.tempId
      );
      if (inputIndex >= 0) {
        emailChangesState.newEmailInputs[inputIndex] = {
          ...emailChangesState.newEmailInputs[inputIndex],
          error: 'Please enter a valid email',
          isEditing: true,
        };
      }
      return { isValid: false, firstInvalidId: input.tempId };
    }
  }

  return { isValid: true, firstInvalidId: null };
};

// Scroll to invalid email element
export const scrollToInvalidEmail = (emailId: string | number | null) => {
  if (emailId === null) return;

  const element = document.getElementById(`email-${emailId}`);
  if (element) {
    element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    // Optionally focus the element
    element.focus();
  }
};

export const EmailsList = ({
  contactData,
  showLabel = true,
}: EmailsListProps) => {
  // Use ref to detect component remount (drawer open/close)
  // Ref is created fresh on each mount, so we can check if it's been initialized
  const hasInitialized = useRef(false);

  // Reset state on mount (runs once per mount, not on re-renders)
  // This clears stale state from previous drawer sessions
  if (!hasInitialized.current) {
    hasInitialized.current = true;
    resetEmailChanges();
  }

  const [editingEmailId, setEditingEmailId] = useState<number | null>(null);
  const [editValue, setEditValue] = useState('');
  const [editError, setEditError] = useState('');
  const toast = useShowToast();
  // Force re-render when module-level state changes
  const [, setForceUpdate] = useState(0);

  // Use module-level state for new email inputs (persists during API calls)
  const newEmailInputs = emailChangesState.newEmailInputs;

  // Helper to update new email inputs and trigger re-render
  const setNewEmailInputs = (
    updater: NewEmailInput[] | ((prev: NewEmailInput[]) => NewEmailInput[])
  ) => {
    if (typeof updater === 'function') {
      emailChangesState.newEmailInputs = updater(
        emailChangesState.newEmailInputs
      );
    } else {
      emailChangesState.newEmailInputs = updater;
    }
    setForceUpdate((prev) => prev + 1);
  };

  const emails = contactData?.emails || [];

  // Store emails at module level for duplicate checking in validateAllEmails
  // This ensures validateAllEmails can check duplicates even when called from outside
  emailChangesState.originalEmails = emails;

  // Check if an email is duplicate (exists in other emails)
  const isDuplicateEmail = (
    emailToCheck: string,
    excludeId?: number | null,
    excludeTempId?: string
  ): boolean => {
    const normalizedEmail = emailToCheck.toLowerCase().trim();

    // Check existing emails (not deleted)
    const existingEmails = emails
      .filter(
        (e) =>
          e.id !== excludeId && !emailChangesState.deletedEmailIds.has(e.id)
      )
      .map((e) => {
        // Use edited value if available
        const edited = emailChangesState.editedEmails.get(e.id);
        return (edited?.email ?? e.email).toLowerCase().trim();
      });

    // Check new emails
    const newEmails = emailChangesState.newEmailInputs
      .filter((input) => input.tempId !== excludeTempId && input.value.trim())
      .map((input) => input.value.toLowerCase().trim());

    const allEmails = [...existingEmails, ...newEmails];
    return allEmails.includes(normalizedEmail);
  };

  // Synchronous validation to prevent race conditions when switching between emails
  const validateEmail = (
    value: string,
    excludeId?: number | null,
    excludeTempId?: string
  ): { isValid: boolean; error: string } => {
    try {
      emailValidationSchema.validateSync(value);

      // Check for duplicates
      if (isDuplicateEmail(value, excludeId, excludeTempId)) {
        const error = 'Email is duplicate for this candidate';
        setEditError(error);
        return { isValid: false, error };
      }

      setEditError('');
      return { isValid: true, error: '' };
    } catch (error: any) {
      setEditError(error.message);
      return { isValid: false, error: error.message };
    }
  };

  const copyToClipboard = async (email: string) => {
    try {
      await navigator.clipboard.writeText(email);
      toast({
        title: 'Email copied to clipboard!',
        status: 'success',
      });
    } catch (err) {
      toast({ title: 'Failed to copy email', status: 'error' });
    }
  };

  // Store make primary locally (no API call)
  const handleMakePrimary = (emailToMakePrimary: Email) => {
    if (emailToMakePrimary.id !== null) {
      emailChangesState.newPrimaryEmailId = emailToMakePrimary.id;
      // Clear any new email primary selection
      emailChangesState.newPrimaryTempId = null;
      setForceUpdate((prev) => prev + 1);
    }
  };

  // Store delete locally and remove from UI (no API call)
  const handleDeleteEmail = (emailToDelete: Email) => {
    if (emailToDelete.id !== null) {
      emailChangesState.deletedEmailIds.add(emailToDelete.id);
      // If deleting the current/new primary, clear it
      if (emailChangesState.newPrimaryEmailId === emailToDelete.id) {
        emailChangesState.newPrimaryEmailId = null;
      }
      setForceUpdate((prev) => prev + 1);
    }
  };

  const handleEditClick = (email: Email) => {
    // Use edited value if available, otherwise use original
    const editedEmail = emailChangesState.editedEmails.get(email.id);
    const value = editedEmail?.email ?? email.email;
    setEditingEmailId(email.id);
    setEditValue(value);
    setEditError(
      editedEmail && !editedEmail.isValid ? 'Please enter a valid email' : ''
    );
    // Track at module level for validation on submit
    emailChangesState.currentlyEditingId = email.id;
    emailChangesState.currentlyEditingTempId = null; // Clear new email tracking
    emailChangesState.currentEditValue = value;
  };

  // Validate and save email on blur (clicking outside) - synchronous to prevent race conditions
  const handleBlurValidation = (emailToEdit: Email) => {
    const trimmedEmail = editValue.trim();

    if (!trimmedEmail) {
      const errorMsg = 'Email is required';
      setEditError(errorMsg);
      // Store as invalid with error message
      if (emailToEdit.id !== null) {
        emailChangesState.editedEmails.set(emailToEdit.id, {
          id: emailToEdit.id,
          email: '',
          isValid: false,
          error: errorMsg,
        });
      }
      // Force re-render to keep error visible
      setForceUpdate((prev) => prev + 1);
      return;
    }

    const { isValid, error } = validateEmail(trimmedEmail, emailToEdit.id);

    // Store the edited email with validation status and error message
    if (emailToEdit.id !== null) {
      emailChangesState.editedEmails.set(emailToEdit.id, {
        id: emailToEdit.id,
        email: trimmedEmail,
        isValid,
        error: isValid ? '' : error,
      });
    }

    // Exit edit mode only if valid, otherwise keep error visible
    if (isValid) {
      setEditingEmailId(null);
      setEditValue('');
      setEditError('');
      // Clear module-level tracking
      emailChangesState.currentlyEditingId = null;
      emailChangesState.currentEditValue = '';
    } else {
      // Keep error visible and force re-render
      setEditError(error);
      setForceUpdate((prev) => prev + 1);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent, emailItem: Email) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleBlurValidation(emailItem);
    } else if (e.key === 'Escape') {
      // On escape, revert changes and exit edit mode
      if (emailItem.id !== null) {
        emailChangesState.editedEmails.delete(emailItem.id);
      }
      setEditingEmailId(null);
      setEditValue('');
      setEditError('');
      // Clear module-level tracking
      emailChangesState.currentlyEditingId = null;
      emailChangesState.currentEditValue = '';
    }
  };

  // Handlers for new email inputs
  const handleAddNewEmail = () => {
    const tempId = `new-${Date.now()}`;
    setNewEmailInputs((prev) => [
      ...prev,
      { tempId, value: '', error: '', isEditing: true },
    ]);
  };

  const handleNewEmailChange = (tempId: string, value: string) => {
    setNewEmailInputs((prev) =>
      prev.map((input) =>
        input.tempId === tempId ? { ...input, value, error: '' } : input
      )
    );
    // Track at module level for validation on submit
    emailChangesState.currentlyEditingId = null; // Clear existing email tracking
    emailChangesState.currentlyEditingTempId = tempId;
    emailChangesState.currentEditValue = value;
  };

  // Synchronous to prevent race conditions when switching between emails
  const handleNewEmailBlur = (tempId: string) => {
    const input = newEmailInputs.find((i) => i.tempId === tempId);
    if (!input) return;

    const trimmedEmail = input.value.trim();

    if (!trimmedEmail) {
      // Remove empty new email input
      setNewEmailInputs((prev) => prev.filter((i) => i.tempId !== tempId));
      // Also remove from module state by tempId
      emailChangesState.newEmails = emailChangesState.newEmails.filter(
        (e) => e.tempId !== tempId
      );
      // Clear module-level tracking
      emailChangesState.currentlyEditingTempId = null;
      emailChangesState.currentEditValue = '';
      return;
    }

    const { isValid, error } = validateEmail(trimmedEmail, null, tempId);

    // Update local state - keep in edit mode with error if invalid
    setNewEmailInputs((prev) =>
      prev.map((i) =>
        i.tempId === tempId
          ? {
              ...i,
              value: trimmedEmail,
              error: isValid ? '' : error,
              isEditing: !isValid, // Stay in edit mode if error
            }
          : i
      )
    );

    // Update module state for form submission
    const existingIndex = emailChangesState.newEmails.findIndex(
      (e) => e.tempId === tempId
    );
    if (existingIndex >= 0) {
      emailChangesState.newEmails[existingIndex] = {
        tempId,
        email: trimmedEmail,
        isValid,
      };
    } else {
      emailChangesState.newEmails.push({
        tempId,
        email: trimmedEmail,
        isValid,
      });
    }

    // Clear module-level tracking if valid
    if (isValid) {
      emailChangesState.currentlyEditingTempId = null;
      emailChangesState.currentEditValue = '';
    } else {
      // Force re-render to ensure error stays visible
      setForceUpdate((prev) => prev + 1);
    }
  };

  const handleNewEmailKeyPress = (e: React.KeyboardEvent, tempId: string) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleNewEmailBlur(tempId);
    } else if (e.key === 'Escape') {
      // Remove the new email input on escape
      setNewEmailInputs((prev) => prev.filter((i) => i.tempId !== tempId));
      // Clear module-level tracking
      emailChangesState.currentlyEditingTempId = null;
      emailChangesState.currentEditValue = '';
    }
  };

  const handleDeleteNewEmail = (tempId: string) => {
    setNewEmailInputs((prev) => prev.filter((i) => i.tempId !== tempId));
    // Remove from module state by tempId
    emailChangesState.newEmails = emailChangesState.newEmails.filter(
      (e) => e.tempId !== tempId
    );
    // If deleting the new primary, clear it
    if (emailChangesState.newPrimaryTempId === tempId) {
      emailChangesState.newPrimaryTempId = null;
    }
    // Clear module-level tracking if deleting the currently editing email
    if (emailChangesState.currentlyEditingTempId === tempId) {
      emailChangesState.currentlyEditingTempId = null;
      emailChangesState.currentEditValue = '';
    }
  };

  // Make a new email primary
  const handleMakeNewEmailPrimary = (tempId: string) => {
    // Clear any existing primary selection (both existing and new)
    emailChangesState.newPrimaryEmailId = null;
    emailChangesState.newPrimaryTempId = tempId;
    setForceUpdate((prev) => prev + 1);
  };

  // Filter out deleted emails from display
  const visibleEmails = emails.filter(
    (email) => !emailChangesState.deletedEmailIds.has(email.id)
  );

  // Calculate if we can add more emails
  // Max 2 emails total (regardless of enriched status)
  const totalEmailCount = visibleEmails.length + newEmailInputs.length;
  const canAddMoreEmails = totalEmailCount < 2;

  return (
    <Box mb="12px">
      {showLabel && (
        <KText color="secondary-700" size="sm-medium" mb="8px">
          Email
        </KText>
      )}
      <Flex flexDirection="column" gap="8px">
        {visibleEmails.map((emailItem) => {
          const isEditing = editingEmailId === emailItem.id;

          // Check if this email has been edited
          const editedEmail = emailChangesState.editedEmails.get(emailItem.id);
          const displayEmail = editedEmail?.email ?? emailItem.email;
          const hasEditError = editedEmail && !editedEmail.isValid;
          const storedError = editedEmail?.error || '';

          // Determine primary status (check local state first, then original)
          // If a new email is set as primary, no existing email is primary
          const isPrimary =
            emailChangesState.newPrimaryTempId !== null
              ? false
              : emailChangesState.newPrimaryEmailId !== null
                ? emailChangesState.newPrimaryEmailId === emailItem.id
                : emailItem?.primary;

          return (
            <Box key={emailItem.id} id={`email-${emailItem.id}`}>
              {isEditing ? (
                <KInput
                  value={editValue}
                  onChange={(e) => {
                    setEditValue(e.target.value);
                    emailChangesState.currentEditValue = e.target.value;
                    if (editError) {
                      setEditError('');
                    }
                  }}
                  onKeyDown={(e) => handleKeyPress(e, emailItem)}
                  onBlur={() => handleBlurValidation(emailItem)}
                  placeholder="Enter email address"
                  autoFocus
                  size="md"
                  fieldType="email"
                  error={editError}
                  isDisabled={emailItem?.enriched}
                  py="24px"
                />
              ) : (
                <>
                  <Box
                    px="12px"
                    py="12px"
                    border="1px solid"
                    borderColor={hasEditError ? 'error-200' : 'border-color'}
                    borderRadius="6px"
                    backgroundColor={
                      emailItem?.enriched ? 'secondary-25' : 'base-white'
                    }
                  >
                    <Flex
                      alignItems="center"
                      justifyContent="space-between"
                      cursor={emailItem.enriched ? 'default' : 'pointer'}
                      onClick={() => {
                        if (!emailItem.enriched) {
                          handleEditClick(emailItem);
                        }
                      }}
                    >
                      <Flex
                        alignItems="center"
                        gap="8px"
                        flex="1"
                        overflow="hidden"
                      >
                        <TruncatedTextWithTooltip
                          text={displayEmail}
                          fontSize="sm"
                          color={hasEditError ? 'error-200' : 'secondary-700'}
                          maxW="100%"
                        />
                        {emailItem?.enriched && (
                          <Icon
                            as={FaCheckCircle as any}
                            color="primary-400"
                            boxSize="14px"
                            flexShrink={0}
                          />
                        )}
                      </Flex>
                      <Flex alignItems="center" gap="6px" h="24px">
                        {!isPrimary ? (
                          <KText
                            color="primary-500"
                            cursor="pointer"
                            size="sm-medium"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleMakePrimary(emailItem);
                            }}
                          >
                            Make primary
                          </KText>
                        ) : (
                          <KTag
                            color="primary-500"
                            bg="primary-25"
                            value="Primary"
                          />
                        )}
                        <Copy03
                          size={16}
                          color="var(--kula-colors-secondary-700)"
                          className="kula-icon"
                          style={{ cursor: 'pointer' }}
                          onClick={(e) => {
                            e.stopPropagation();
                            copyToClipboard(emailItem.email);
                          }}
                        />
                        <Trash03
                          size={16}
                          color="var(--kula-colors-secondary-700)"
                          className="kula-icon"
                          style={{ cursor: 'pointer' }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteEmail(emailItem);
                          }}
                        />
                      </Flex>
                    </Flex>
                  </Box>
                  {/* Show error message in view mode */}
                  {hasEditError && storedError && (
                    <KText size="sm" color="error-200" mt="4px">
                      {storedError}
                    </KText>
                  )}
                </>
              )}
            </Box>
          );
        })}

        {/* New email inputs */}
        {newEmailInputs.map((newInput) => {
          const isNewEmailPrimary =
            emailChangesState.newPrimaryTempId === newInput.tempId;

          return (
            <Box key={newInput.tempId} id={`email-${newInput.tempId}`}>
              {newInput.isEditing ? (
                <KInput
                  value={newInput.value}
                  onChange={(e) =>
                    handleNewEmailChange(newInput.tempId, e.target.value)
                  }
                  onKeyDown={(e) => handleNewEmailKeyPress(e, newInput.tempId)}
                  onBlur={() => handleNewEmailBlur(newInput.tempId)}
                  placeholder="Enter email address"
                  autoFocus
                  size="md"
                  fieldType="email"
                  error={newInput.error}
                  py="24px"
                />
              ) : (
                <>
                  <Box
                    px="12px"
                    py="12px"
                    border="1px solid"
                    borderColor={newInput.error ? 'error-200' : 'border-color'}
                    borderRadius="6px"
                    backgroundColor="base-white"
                  >
                    <Flex
                      alignItems="center"
                      justifyContent="space-between"
                      cursor="pointer"
                      onClick={() =>
                        setNewEmailInputs((prev) =>
                          prev.map((i) =>
                            i.tempId === newInput.tempId
                              ? { ...i, isEditing: true }
                              : i
                          )
                        )
                      }
                    >
                      <Flex
                        alignItems="center"
                        gap="8px"
                        flex="1"
                        overflow="hidden"
                      >
                        <TruncatedTextWithTooltip
                          text={newInput.value}
                          fontSize="sm"
                          color={newInput.error ? 'error-200' : 'secondary-700'}
                          maxW="100%"
                        />
                      </Flex>
                      <Flex alignItems="center" gap="6px" h="24px">
                        {!isNewEmailPrimary ? (
                          <KText
                            color="primary-500"
                            cursor="pointer"
                            size="sm-medium"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleMakeNewEmailPrimary(newInput.tempId);
                            }}
                          >
                            Make primary
                          </KText>
                        ) : (
                          <KTag
                            color="primary-500"
                            bg="primary-25"
                            value="Primary"
                          />
                        )}
                        <Copy03
                          size={16}
                          color="var(--kula-colors-secondary-700)"
                          className="kula-icon"
                          style={{ cursor: 'pointer' }}
                          onClick={(e) => {
                            e.stopPropagation();
                            copyToClipboard(newInput.value);
                          }}
                        />
                        <Trash03
                          size={16}
                          color="var(--kula-colors-secondary-700)"
                          className="kula-icon"
                          style={{ cursor: 'pointer' }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteNewEmail(newInput.tempId);
                          }}
                        />
                      </Flex>
                    </Flex>
                  </Box>
                  {/* Show error message in view mode */}
                  {newInput.error && (
                    <KText size="sm" color="error-200" mt="4px">
                      {newInput.error}
                    </KText>
                  )}
                </>
              )}
            </Box>
          );
        })}
      </Flex>

      {/* Add email button */}
      {canAddMoreEmails && (
        <Button
          variant="quaternary"
          leftIcon={<PlusCircle size={16} className="kula-icon" />}
          size="sm"
          color="secondary-700"
          onClick={handleAddNewEmail}
          width="fit-content"
          mt={
            visibleEmails.length > 0 || newEmailInputs.length > 0 ? '8px' : '0'
          }
        >
          Add email
        </Button>
      )}
    </Box>
  );
};
